import {
  bounds,
  boundsIntersect,
  carrier,
  center,
  closestPoint,
  endpoints,
  footOnCarrier,
  intersect,
  liesOn,
  midpoint,
  perpendicularFrom,
  quadrants,
  tangentFrom,
  vec,
  type Anchor,
  type Shape,
  type Vec2,
} from '../geom';
import type { NodeId } from '../model';
import type { ResolvedEntity } from '../scene';

export interface ProviderContext {
  readonly pointer: Vec2;
  /** The tool's pending point, which perpendicular and tangent snaps measure from. */
  readonly reference: Anchor | null;
  readonly gridSize: number;
  readonly nearby: readonly ResolvedEntity[];
}

/** A point a snap kind offers, before the engine ranks it against the others. */
export interface SnapPoint {
  readonly point: Vec2;
  readonly sourceIds: readonly NodeId[];
}

interface SnapDefinition {
  readonly kind: string;
  readonly label: string;
  /** The letter that chooses this kind while a point is being placed. */
  readonly shortcut: string;
  readonly provide: (ctx: ProviderContext) => SnapPoint[];
}

const perEntity =
  (extract: (entity: ResolvedEntity, ctx: ProviderContext) => Vec2[]) =>
  (ctx: ProviderContext): SnapPoint[] =>
    ctx.nearby.flatMap((entity) =>
      extract(entity, ctx).map((point) => ({ point, sourceIds: [entity.id] })),
    );

const one = (point: Vec2 | null): Vec2[] => (point ? [point] : []);

function intersections(ctx: ProviderContext): SnapPoint[] {
  const found: SnapPoint[] = [];
  for (let i = 0; i < ctx.nearby.length; i += 1) {
    const a = ctx.nearby[i] as ResolvedEntity;
    const boundsA = bounds(a.shape);
    for (let j = i + 1; j < ctx.nearby.length; j += 1) {
      const b = ctx.nearby[j] as ResolvedEntity;
      if (!boundsIntersect(boundsA, bounds(b.shape))) continue;
      for (const point of intersect(a.shape, b.shape))
        found.push({ point, sourceIds: [a.id, b.id] });
    }
  }
  return found;
}

/**
 * With nothing yet to measure from, the touch point is not known, so a shape offers the
 * point on it nearest the pointer; a tool that solves tangency later reads the shape from
 * the snap's source.
 */
function deferredTangent(shape: Shape, pointer: Vec2): Vec2[] {
  const point = footOnCarrier(carrier(shape), pointer);
  return point && liesOn(shape, point) ? [point] : [];
}

function gridPoint(ctx: ProviderContext): SnapPoint[] {
  if (ctx.gridSize <= 0) return [];
  const point = vec(
    Math.round(ctx.pointer.x / ctx.gridSize) * ctx.gridSize,
    Math.round(ctx.pointer.y / ctx.gridSize) * ctx.gridSize,
  );
  return [{ point, sourceIds: [] }];
}

/**
 * Every snap kind, in the order the snap keys list them. Only the chosen kind snaps. A new
 * kind is one entry here plus a glyph in the renderer.
 */
export const SNAP_DEFINITIONS = [
  {
    kind: 'endpoint',
    label: 'Endpoint',
    shortcut: 'E',
    provide: perEntity((entity) => endpoints(entity.shape)),
  },
  {
    kind: 'intersection',
    label: 'Intersection',
    shortcut: 'I',
    provide: intersections,
  },
  {
    kind: 'midpoint',
    label: 'Midpoint',
    shortcut: 'M',
    provide: perEntity((entity) => one(midpoint(entity.shape))),
  },
  {
    kind: 'center',
    label: 'Center',
    shortcut: 'C',
    provide: perEntity((entity) => one(center(entity.shape))),
  },
  {
    kind: 'quadrant',
    label: 'Quadrant',
    shortcut: 'Q',
    provide: perEntity((entity) => quadrants(entity.shape)),
  },
  {
    kind: 'perpendicular',
    label: 'Perpendicular',
    shortcut: 'P',
    // Around an anchor, these are also where a circle touches a shape, so tangent offers them
    // too; either letter works.
    provide: perEntity((entity, ctx) =>
      ctx.reference ? perpendicularFrom(entity.shape, ctx.reference.point) : [],
    ),
  },
  {
    kind: 'tangent',
    label: 'Tangent',
    shortcut: 'T',
    provide: perEntity((entity, ctx) => {
      // Touching a curve, the near end is not fixed yet, so tangent is deferred as with no anchor.
      if (!ctx.reference || ctx.reference.mode === 'touching') {
        return deferredTangent(entity.shape, ctx.pointer);
      }
      const { point, mode } = ctx.reference;
      // A line from the anchor grazes a curve; a circle around it touches one where the
      // shortest path from the anchor meets the curve at a right angle.
      return mode === 'line'
        ? tangentFrom(entity.shape, point)
        : perpendicularFrom(entity.shape, point);
    }),
  },
  {
    kind: 'nearest',
    label: 'Nearest',
    shortcut: 'N',
    provide: perEntity((entity, ctx) => [closestPoint(entity.shape, ctx.pointer)]),
  },
  { kind: 'grid', label: 'Grid', shortcut: 'G', provide: gridPoint },
] as const satisfies readonly SnapDefinition[];

export type SnapKind = (typeof SNAP_DEFINITIONS)[number]['kind'];

export const SNAP_LABELS = Object.fromEntries(
  SNAP_DEFINITIONS.map(({ kind, label }) => [kind, label]),
) as Readonly<Record<SnapKind, string>>;

export const snapForShortcut = (key: string): SnapKind | null =>
  SNAP_DEFINITIONS.find((definition) => definition.shortcut.toLowerCase() === key.toLowerCase())
    ?.kind ?? null;
